import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Login from './components/Auth/Login';
import UserManagement from './components/Admin/UserManagement';
import AdminDashboard from './components/Admin/AdminDashboard';
import PaymentPage from './pages/PaymentPage';
import UserPaymentPage from './pages/UserPaymentPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AnalyticsHistoryPage from './pages/AnalyticsHistoryPage';
import CreateUser from './components/Auth/CreateUser';
import CardList from './components/Card/CardList';
import CardForm from './components/Card/CardForm';
import MainLayout from './components/Layout/MainLayout';
import PublicCard from './components/Card/PublicCard';
import ProtectedRoute from './components/common/ProtectedRoute';
import CardManagement from './components/Admin/CardManagement';
import LandingPage from './components/Landing/LandingPage';

import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { fetchCards, createCard as apiCreate, updateCard as apiUpdate, deleteCard as apiDelete, fetchPublicCard } from './api/cardApi';
import { generateSlug } from './utils/urlUtils';

const PublicCardWrapper = () => {
  const { slug } = useParams();
  const [card, setCard] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchPublicCard(slug);
        setCard(data);
      } catch (err) {
        setNotFound(true);
      }
    };
    load();
  }, [slug]);

  if (notFound) return <Navigate to="/" />;
  if (!card) return <div style={{ padding: 20 }}>Loading...</div>;
  return <PublicCard card={card} />;
};

function AppRoutes() {
  const { token, role, loading: authLoading, isPaymentEnabled } = useAuth();
  const [cards, setCards] = useState([]);

  useEffect(() => {
    if (!token || !role) {
      return;
    }
    const load = async () => {
      try {
        const data = await fetchCards();
        setCards(data);
      } catch (err) {
        console.error('Failed to fetch cards', err);
      }
    };
    load();
  }, [token, role]);

  const refreshCards = async () => {
    const data = await fetchCards();
    setCards(data);
  };

  const handleCreateCard = async (cardData) => {
    const payload = { ...cardData };
    if (!payload.slug) {
      payload.slug = generateSlug(payload.name, payload.companyName);
    }
    await apiCreate(payload);
    await refreshCards();
  };

  const handleEditCard = async (cardData) => {
    await apiUpdate(cardData.id, cardData);
    await refreshCards();
  };

  const handleDeleteCard = async (cardId) => {
    await apiDelete(cardId);
    await refreshCards();
  };

  const handleToggleVisibility = async (cardId) => {
    const card = cards.find((c) => c.id === cardId);
    if (!card) return;
    await apiUpdate(cardId, { isPublished: !card.isPublished });
    await refreshCards();
  };

  if (authLoading) {
    return <div style={{ padding: 20 }}>Authenticating...</div>;
  }
  
  return (
    <Routes>
      {/* Public routes that do not require login */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        token ? <Navigate to={role === 'admin' ? '/admin/dashboard' : '/dashboard'} /> : <Login />
      } />
      <Route path="/create-user" element={
        token ? <Navigate to={role === 'admin' ? '/admin/dashboard' : '/dashboard'} /> : <CreateUser />
      } />
      <Route path="/card/:slug" element={<PublicCardWrapper />} />
      
      {/* Protected routes that require login */}
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route path="/home" element={<Navigate to={role === 'admin' ? '/admin/dashboard' : '/dashboard'} />} />
          <Route path="/dashboard" element={<CardList cards={cards} onDelete={handleDeleteCard} onToggleVisibility={handleToggleVisibility} />} />
          <Route path="/edit-card/:slug" element={<CardForm onSaveSuccess={refreshCards} />} />
          <Route path="/create-card" element={<CardForm onSaveSuccess={refreshCards} />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/analytics-history" element={<AnalyticsHistoryPage />} />
          {isPaymentEnabled && <Route path="/payment" element={<UserPaymentPage />} />}

          {/* Admin Only Routes */}
          <Route element={<ProtectedRoute requiredRole="admin" />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/user-management" element={<UserManagement />} />
            {isPaymentEnabled && <Route path="/admin/payments" element={<PaymentPage />} />}
            <Route path="/admin/cards" element={<CardManagement />} />
            <Route path="/admin/analytics" element={<AnalyticsPage />} />
            <Route path="/admin/analytics-history" element={<AnalyticsHistoryPage />} />
          </Route>
        </Route>
      </Route>

      {/* Catch all route - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const { activeTheme } = useTheme();

  return (
    <MuiThemeProvider theme={activeTheme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </MuiThemeProvider>
  );
}

export default App;
